/**
 * Carte des services — Leaflet
 */

import L from 'leaflet';
import markerIconUrl from '../../../images/before-title-h1.svg';

import 'leaflet/dist/leaflet.css';

const MANCHE_CENTER = [49.1, -1.35];
const MANCHE_ZOOM = 9;

const markerIcon = L.icon({
    iconUrl: markerIconUrl,
    iconSize: [18, 26],
    iconAnchor: [9, 26],
    popupAnchor: [0, -26],
});

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

/**
 * @param {number} latitude
 * @param {number} longitude
 * @returns {string}
 */
function buildDirectionsUrl(latitude, longitude) {
    const params = new URLSearchParams({
        api: '1',
        destination: `${latitude},${longitude}`,
        travelmode: 'driving',
    });

    return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * @param {HTMLElement} element
 */
function initMap(element) {
    if (element.dataset.carteServicesInitialized === 'true') {
        return;
    }

    const rawPoints = element.dataset.points;

    if (!rawPoints) {
        return;
    }

    /** @type {Array<{ titre: string, adresse?: string, latitude: number, longitude: number }>} */
    let points;

    try {
        points = JSON.parse(rawPoints);
    } catch {
        return;
    }

    if (!Array.isArray(points) || points.length === 0) {
        return;
    }

    const directionsLabel = element.dataset.directionsLabel || 'Mon itinéraire';

    const map = L.map(element, {
        scrollWheelZoom: false,
    }).setView(MANCHE_CENTER, MANCHE_ZOOM);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
    }).addTo(map);

    const markers = [];

    points.forEach((point) => {
        if (
            typeof point.latitude !== 'number'
            || typeof point.longitude !== 'number'
        ) {
            return;
        }

        const marker = L.marker([point.latitude, point.longitude], {
            icon: markerIcon,
        }).addTo(map);
        const titre = typeof point.titre === 'string' ? point.titre : '';
        const adresse = typeof point.adresse === 'string' ? point.adresse : '';

        let popupContent = '';

        if (titre !== '') {
            popupContent += `<strong>${escapeHtml(titre)}</strong>`;
        }

        if (adresse !== '') {
            popupContent += `<div class="carte-services__popup-adresse">${adresse}</div>`;
        }

        const directionsUrl = buildDirectionsUrl(point.latitude, point.longitude);

        popupContent += `<div class="wp-block-button is-style-primary"><a class="wp-block-button__link" href="${escapeHtml(directionsUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(directionsLabel)}</a></div>`;

        if (popupContent !== '') {
            marker.bindPopup(popupContent);
        }

        markers.push(marker);
    });

    if (markers.length === 1) {
        map.setView(markers[0].getLatLng(), 12);
    } else if (markers.length > 1) {
        map.fitBounds(L.featureGroup(markers).getBounds().pad(0.15));
    }

    element.dataset.carteServicesInitialized = 'true';
}

/**
 * Initialise toutes les cartes présentes sur la page.
 */
export function initCarteServicesMaps() {
    document.querySelectorAll('[data-carte-services-map]').forEach((element) => {
        if (element instanceof HTMLElement) {
            initMap(element);
        }
    });
}
